/**
 * Where the admin keeps its auth tokens. "Remember me" on the login page decides the store:
 * checked → localStorage (survives closing the browser), unchecked → sessionStorage (cleared when
 * the browser session ends). Reads look in the session store first, then the persistent one.
 */
const ACCESS = "accessToken";
const REFRESH = "refreshToken";
const USER = "user";

function safe<T>(fn: () => T, fallback: T): T {
	try {
		return fn();
	} catch {
		return fallback;
	}
}

export const authStorage = {
	getAccessToken(): string | null {
		return safe(() => sessionStorage.getItem(ACCESS) ?? localStorage.getItem(ACCESS), null);
	},

	getRefreshToken(): string | null {
		return safe(() => sessionStorage.getItem(REFRESH) ?? localStorage.getItem(REFRESH), null);
	},

	/** Stores a fresh login, in the store chosen by "Remember me", and clears the other one. */
	setTokens(accessToken: string, refreshToken: string, remember: boolean) {
		safe(() => {
			const target = remember ? localStorage : sessionStorage;
			const other = remember ? sessionStorage : localStorage;
			other.removeItem(ACCESS);
			other.removeItem(REFRESH);
			target.setItem(ACCESS, accessToken);
			target.setItem(REFRESH, refreshToken);
		}, undefined);
	},

	/** Saves a refreshed access token next to the refresh token it came from. */
	setAccessToken(accessToken: string) {
		safe(() => {
			const store = sessionStorage.getItem(REFRESH) !== null ? sessionStorage : localStorage;
			store.setItem(ACCESS, accessToken);
		}, undefined);
	},

	clear() {
		safe(() => {
			for (const store of [localStorage, sessionStorage]) {
				store.removeItem(ACCESS);
				store.removeItem(REFRESH);
				store.removeItem(USER);
			}
		}, undefined);
	},
};
